const _serviceDependencies = ['$http', '$q', 'Util'];
/**
 * @ngdoc service
 * @name rest.services.BaseRestService
 *
 * @description
 * This class contains generic methods for the REST services.
 */
export default class BaseRestService {

    /**
     * @param  {any} $http
     * @param  {any} $q
     * @param  {any} Util
     */
    constructor($http, $q, Util) {

        this.$http = $http;
        this.Util = Util;
        this.$q = $q;

    }

    /**
     * @ngdoc function
     * @name rest.services.BaseRestService#create
     * @methodOf rest.services.BaseRestService
     * @function
     *
     * @description
     * Creates a resource
     *
     * @param {object} payload Resource payload
     * @param {string} path API Resource path
     *
     * @returns {Promise} The promise of the call.
     */
    create(payload, path, extraHeaders) {

        const headers = this.getDefaultHeaders(extraHeaders);

        return this.$http({
            method: 'POST',
            url: `${CWCONFIG.restApi.url}${path}`,
            data: payload || {},
            timeout: CWCONFIG.timeouts.httpTimeout,
            headers,
        });

    }

    /**
     * @ngdoc function
     * @name rest.services.BaseRestService#post
     * @methodOf rest.services.BaseRestService
     * @function
     *
     * @description
     * Creates a resource
     *
     * @param {object} payload Resource payload
     * @param {string} path API Resource path
     *
     * @returns {Promise} The promise of the call.
     */
    post(payload, path, extraHeaders) {

        const headers = this.getDefaultHeaders(extraHeaders);

        return this.$http({
            method: 'POST',
            url: CWCONFIG.restApi.url + path,
            data: payload || {},
            timeout: CWCONFIG.timeouts.httpTimeout,
            headers,
        });

    }

    /**
     * @ngdoc function
     * @name rest.services.BaseRestService#update
     * @methodOf rest.services.BaseRestService
     * @function
     *
     * @description
     * Updates a resource
     *
     * @param {string} resourceId Resource identifier
     * @param {object} payload Resource payload
     * @param {string} path API Resource path
     *
     * @returns {Promise} The promise of the call.
     */
    update(resourceId, payload, path, extraHeaders) {

        const headers = this.getDefaultHeaders(extraHeaders);

        return this.$http({
            method: 'PUT',
            url: `${CWCONFIG.restApi.url}${path}${resourceId || ''}`,
            timeout: CWCONFIG.timeouts.httpTimeout,
            data: payload || {},
            headers,
        });

    }

    /**
     * @ngdoc function
     * @name rest.services.BaseRestService#remove
     * @methodOf rest.services.BaseRestService
     * @function
     *
     * @description
     * Remove a resource
     *
     * @param {string} resourceId Resource identifier
     * @param {string} path API Resource path
     *
     * @returns {Promise} The promise of the call.
     */
    remove(resourceId, path, extraHeaders) {

        const headers = this.getDefaultHeaders(extraHeaders);

        return this.$http({
            method: 'DELETE',
            timeout: CWCONFIG.timeouts.httpTimeout,
            url: `${CWCONFIG.restApi.url}${path}${resourceId}`,
            data: {},
            headers,
        });

    }

    /**
     * @ngdoc function
     * @name rest.services.BaseRestService#getAllAsync
     * @methodOf rest.services.BaseRestService
     * @function
     *
     * @description
     * Retrieves resources from the backend.
     *
     * @param {string} path API Resource path
     *
     * @returns {Promise} The promise of the call.
     */
    getAllAsync(path) {

        const nextLinkAttr = 'data.links.next';

        let requestResponse;
        const requestDeferred = this.$q.defer();

        const concatResponseData = (response) => {

            if (!requestResponse) {

                requestResponse = response;

            } else {

                if (requestResponse.data.data) {

                    requestResponse.data.data =
                        requestResponse.data.data.concat(response.data.data);

                }

                if (requestResponse.data.included) {

                    requestResponse.data.included =
                        requestResponse.data.included.concat(response.data.included);

                }

            }

        };


        const loadNextResourcesAsync = (nextUrl) => {

            const resourcesDeferred = this.$q.defer();

            const loadNextResource = (resource) => {

                if (resource) {

                    this._getResourceAsync(resource)
                        .then((response) => {

                            concatResponseData(response);
                            loadNextResource(this.Util.deepFind(response, nextLinkAttr));

                        })
                        .then(null, (error) => {

                            resourcesDeferred.reject(error);

                        });

                } else {

                    resourcesDeferred.resolve();

                }

            };

            loadNextResource(nextUrl);

            return resourcesDeferred.promise;

        };

        const headers = this.getDefaultHeaders();

        this.$http({
            method: 'GET',
            url: `${CWCONFIG.restApi.url}${path}`,
            timeout: CWCONFIG.timeouts.httpTimeout,
            headers,
        })
            .then((response) => {

                concatResponseData(response);

                loadNextResourcesAsync(this.Util.deepFind(response, nextLinkAttr))
                    .then(() => {

                        requestDeferred.resolve(requestResponse);

                    })
                    .then(null, (error) => {

                        requestDeferred.reject(error);

                    });

            }).then(null, (error) => {

                requestDeferred.reject(error);

            });

        return requestDeferred.promise;

    }

    /**
     * @ngdoc function
     * @name rest.services.BaseRestService#getPageAsync
     * @methodOf rest.services.BaseRestService
     * @function
     *
     * @description
     * Retrieves a single page of resources from the backend.
     *
     * @param {string} path API Resource path
     * @param {string} pageLink The link to the requested page
     *
     * @returns {Promise} The promise of the call.
     */
    getPageAsync(path, pageLink) {

        const requestDeferred = this.$q.defer();

        if (!pageLink) {

            // first page
            pageLink = `${CWCONFIG.restApi.url}${path}`;

        }

        this._getResourceAsync(pageLink)
            .then((response) => {

                requestDeferred.resolve(response);

            })
            .then(null, (error) => {

                requestDeferred.reject(error);

            });

        return requestDeferred.promise;

    }


    _getResourceAsync(url) {

        const headers = this.getDefaultHeaders();

        return this.$http({
            method: 'GET',
            timeout: CWCONFIG.timeouts.httpTimeout,
            url,
            headers,
        });

    }

    getDefaultHeaders(extraHeaders) {

        const token = this.Util.getToken();
        let headers = {};

        // Check if the Authorization header is already set
        const authorizationHeader = this.Util.deepFind(extraHeaders, 'Authorization');
        if (!authorizationHeader) {

            headers = {
                Authorization: (token) ?
                    `Bearer ${token}` :
                    `Basic ${this.Util.base64encode(CWCONFIG.oauthKey)}`,
            };

        }

        if (typeof extraHeaders !== 'undefined') {

            headers = angular.extend(headers, extraHeaders);

        }

        return headers;

    }

}

_serviceDependencies.push(BaseRestService);

// angular.module('rest.services')
//     .service('BaseRestService', _serviceDependencies);

BaseRestService.$inject = ['$http', '$q', 'Util'];

angular.module('rest.services')
    .service('BaseRestService', BaseRestService);



// WEBPACK FOOTER //
// ./scripts/restServices/baseRestService.js